import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	Avatar,
	Box,
	ChatAltOutline,
	ClappingHandsOutline,
	DotsHorizontalSolid,
	HistoryOutline,
	HStack,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	rem,
	SpotlightOutline,
	TargetOutline,
	Text,
	useDisclosure,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, usePermission } from '@bonusly/who-am-i';

import { CreateReviewModal } from '../../../../components/growth/review-recap/components/create-review-modal';
import { GiveBoxEvent } from '../../../../components/recognition/give-box/events';
import { CreateUserSpotlightModal } from '../../../../components/spotlight';
import { useSpotlightMenuAction } from '../../../../components/spotlight/hooks/use-spotlight-menu-action';
import { useCreateGoalModal } from '../../../../modules/growth/components/goals';
import { DirectReport } from '../../../../types/user';
import { UserNavLink } from '../user-nav-link';

interface DirectReportMenuProps {
	directReport: DirectReport;
}

export const DirectReportMenu: FC<DirectReportMenuProps> = ({ directReport }) => {
	const { t } = useTranslation(['navigation', 'one_on_ones', 'common']);
	const canAccessGoals = usePermission(Permission.CanAccessGoals);
	const dispatch = useDispatch();

	const spotlight = useSpotlightMenuAction({ userId: directReport.id, displayName: directReport.displayName, entryPoint: 'sidebar_team_menu' });

	// Modals
	const createReviewModal = useDisclosure();
	const { onOpen: onOpenGoalModal } = useCreateGoalModal();

	const handleRecognize = () => {
		dispatch(GiveBoxEvent.Open, {
			recipient: { id: directReport.id, username: directReport.username },
		});
	};

	const handleGiveFeedback = () => {
		dispatch(FeedbackEvent.OpenUnsolicited, {
			defaultUsers: [
				{
					id: directReport.id,
					username: directReport.username,
				},
			],
		});
	};

	return (
		<>
			<HStack justifyContent="space-between" alignItems="center" w="100%" position="relative">
				<UserNavLink to={`/team/${directReport.id}`} data-testid={`nav-link-view-${directReport.id}`}>
					<HStack spacing={rem(12)}>
						<Avatar name={directReport.displayName} src={directReport.profilePic} size="sm" />
						<Text fontSize={100} fontWeight="medium">
							{directReport.displayName}
						</Text>
					</HStack>
				</UserNavLink>

				{/* Actions menu - positioned outside the link for proper accessibility */}
				<Box position="absolute" right={rem(10)}>
					<Menu placement="right-start">
						<MenuButton
							as={IconButton}
							aria-label={t('navigation:teamMenu.menuLabel')}
							variant="ghost"
							size="sm"
							icon={<DotsHorizontalSolid size="sm" />}
							_hover={{ backgroundColor: 'secondary.bgAlpha' }}
							_active={{ backgroundColor: 'secondary.bgAlpha' }}
							_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'md', outlineOffset: '-2px' }}
						/>
						<Portal>
							<MenuList
								width="fit-content"
								zIndex={99999}
								px={rem(6)}
								py={rem(8)}
								sx={{
									'& > *:not(:last-child)': { mb: rem(4) },
									'& [role="menuitem"]': {
										minHeight: rem(40),
									},
									'& [role="menuitem"]:focus-visible': {
										outline: '2px solid',
										outlineColor: 'border.focus',
										outlineOffset: '-2px',
										borderRadius: 'sm',
										backgroundColor: 'bg.surface',
									},
								}}
							>
								{/* Give recognition */}
								<ProtectedContent permissions={Permission.CanGiveRecognition}>
									<MenuItem onClick={handleRecognize}>
										<HStack gap={2}>
											<ClappingHandsOutline />
											<Text>{t('navigation:teamMenu.giveRecognition')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>

								{/* Create goal */}
								<ProtectedContent permissions={Permission.CanAccessGoals}>
									{canAccessGoals && (
										<MenuItem onClick={() => onOpenGoalModal(directReport.id)}>
											<HStack gap={2}>
												<TargetOutline />
												<Text>{t('navigation:teamMenu.createGoal')}</Text>
											</HStack>
										</MenuItem>
									)}
								</ProtectedContent>

								{/* Share feedback */}
								<ProtectedContent permissions={Permission.CanAccessAdvice}>
									<MenuItem onClick={handleGiveFeedback}>
										<HStack gap={2}>
											<ChatAltOutline />
											<Text>{t('navigation:teamMenu.shareFeedback')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>

								{/* Create custom recap */}
								<ProtectedContent permissions={Permission.CanAccessRecaps}>
									<MenuItem onClick={createReviewModal.onOpen}>
										<HStack gap={2}>
											<HistoryOutline />
											<Text>{t('navigation:teamMenu.createCustomRecap')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>

								{spotlight.isEligible && (
									<MenuItem onClick={spotlight.openModal}>
										<HStack gap={2}>
											<SpotlightOutline />
											<Text>{t('navigation:teamMenu.createSpotlight')}</Text>
										</HStack>
									</MenuItem>
								)}
							</MenuList>
						</Portal>
					</Menu>
				</Box>
			</HStack>

			{/* Modals */}
			<CreateReviewModal userId={directReport.id} {...createReviewModal} />
			{spotlight.isEligible && (
				<CreateUserSpotlightModal
					isOpen={spotlight.isModalOpen}
					onClose={spotlight.closeModal}
					targetUserId={spotlight.targetUserId}
					targetUserName={spotlight.targetUserName}
					onSuccess={spotlight.closeModal}
					entryPoint={spotlight.entryPoint}
				/>
			)}
		</>
	);
};
